import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Reveal, { MaskedLine } from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import { PRINCIPLES } from "@/data/content";

export default function About() {
  return (
    <div data-testid="about-page">
      <header className="pt-40 pb-16 md:pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal>
            <p className="flex items-center gap-4 text-xs uppercase tracking-[0.35em] text-cobalt font-medium">
              <span className="h-px w-10 bg-cobalt/60" /> About
            </p>
          </Reveal>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink leading-[1.08]" data-testid="about-heading">
            <MaskedLine delay={0.1}>The firm behind</MaskedLine>
            <MaskedLine delay={0.25}>
              the <em className="text-cobalt">models</em>.
            </MaskedLine>
          </h1>
        </div>
      </header>

      <section className="border-t border-ink/10 py-20 md:py-28" data-testid="about-story">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <SectionHeading eyebrow="Since 2026" title="Independent by design." />
          </div>
          <div className="md:col-span-6 md:col-start-7 space-y-6">
            <Reveal>
              <p className="text-ink/70 text-base md:text-lg leading-relaxed">
                Axiom was founded by systems engineers and machine-learning researchers who kept
                watching the same failure: brilliant models, deployed carelessly, on infrastructure
                nobody understood. The firm exists to close that gap.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-ink/60 leading-relaxed">
                Today we are a consultancy in India, owned by people ready to innovate new solutions
                across the AI landscape. We work with small and mid-sized businesses that want the
                benefits of AI without hiring a technical department.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32" data-testid="principles-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionHeading eyebrow="Principles" title="What we will not compromise." />
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
            {PRINCIPLES.map((p, i) => (
              <Reveal key={p.num} delay={i * 0.07} className="bg-paper">
                <div className="p-8 md:p-10 h-full" data-testid={`principle-${p.num}`}>
                  <span className="font-display text-4xl text-cobalt/70">{p.num}</span>
                  <h3 className="mt-6 font-display text-2xl text-ink">{p.title}</h3>
                  <p className="mt-3 text-sm text-ink/60 leading-relaxed">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-28 border-t border-ink/10" data-testid="about-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-wrap items-center justify-between gap-8">
          <Reveal>
            <h2 className="font-display text-3xl md:text-5xl tracking-tight text-ink">
              Work with people who<br /><em className="text-cobalt">read the papers</em> — and write them.
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <Link
              to="/contact"
              data-testid="about-cta-button"
              className="inline-flex items-center gap-3 rounded-full bg-ink text-paper px-8 py-4 text-sm tracking-wide transition-all duration-200 hover:bg-cobalt hover:scale-[0.98]"
            >
              Get in touch <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
